/**
 * 광고 상세/등록 페이지
 *
 * - id === 'new' : 신규 등록 모드 (isEditing 초기값 true)
 * - 그 외        : 상세 조회/수정 모드
 *
 * 광고 데이터는 목록에서 react-router state로 전달받는다.
 * 수정 성공 후에는 페이지 이동 없이 history.state를 직접 갱신하여
 * 브라우저 뒤로가기 시에도 최신 데이터가 유지되도록 한다.
 */
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { adverService } from '../../services/adverService';
import { useState, useEffect, useRef } from 'react';
import { Adver } from '../../types/adver';
import ConfirmModal from '../../components/ui/ConfirmModal';
import { InputField, SelectField } from '../../components/ui/FormField';
import DetailActionButtons from '../../components/ui/DetailActionButtons';
import BackButton from '../../components/ui/BackButton';
import EmptyDetailState from '../../components/ui/EmptyDetailState';
import { formatDateTime } from '../../utils/format';

export default function AdverDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  const isNewAdver = id === 'new';

  // 목록에서 Link state로 주입된 adver 객체 (직접 URL 진입 시 undefined)
  const locationAdver = location.state?.adver as Adver | undefined;
  const adver = isNewAdver ? null : locationAdver;

  const [isEditing, setIsEditing] = useState(isNewAdver);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [imageFile, setImageFile] = useState<File | undefined>(undefined);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [formData, setFormData] = useState<Partial<Adver>>({
    title: '',
    imageUrl: '',
    linkUrl: '',
    adType: 'BANNER',
    isVisible: true,
    sortOrder: 1,
  });

  useEffect(() => {
    if (adver && !isNewAdver) {
      setFormData(adver);
    }
  }, [adver, isNewAdver]);

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setImageFile(file);
    setImagePreview(URL.createObjectURL(file));
  };

  const handleCancelEdit = () => {
    setIsEditing(false);
    setImageFile(undefined);
    setImagePreview(null);
    // file input의 value를 초기화해야 같은 파일을 다시 선택할 수 있음
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const createMutation = useMutation({
    mutationFn: ({ formData, imageFile }: { formData: any; imageFile?: File }) =>
      adverService.createAdver(formData, imageFile),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['advers'] });
      navigate('/admin/adver');
    },
    onError: () => {
      setIsSubmitting(false);
      alert('광고 등록에 실패했습니다. 다시 시도해주세요.');
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ formData, imageFile }: { formData: any; imageFile?: File }) =>
      adverService.updateAdver(Number(id), formData, imageFile),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['advers'] });
      setIsEditing(false);
      setIsSubmitting(false);
      setImageFile(undefined);
      setImagePreview(null);
      // 페이지 이동 없이 history.state를 최신 formData로 덮어써서
      // 뒤로가기 재진입 시에도 수정된 값이 보이도록 한다
      window.history.replaceState(
        { ...window.history.state, usr: { adver: { ...adver, ...formData } } },
        ''
      );
    },
    onError: () => {
      setIsSubmitting(false);
      alert('광고 수정에 실패했습니다. 다시 시도해주세요.');
    },
  });

  const deleteMutation = useMutation({
    mutationFn: () => adverService.deleteAdver(Number(id)),
    onSuccess: () => navigate('/admin/adver'),
  });

  const handleEdit = () => {
    setIsEditing(true);
    setFormData(adver || {});
  };

  const handleSave = () => {
    if (isSubmitting || createMutation.isPending || updateMutation.isPending) return;
    if (!formData.title || !formData.linkUrl) {
      alert('제목과 링크 URL을 입력해주세요.');
      return;
    }
    setIsSubmitting(true);
    if (isNewAdver) {
      createMutation.mutate({
        formData: {
          title: formData.title,
          linkUrl: formData.linkUrl,
          adType: formData.adType || 'BANNER',
          isVisible: formData.isVisible ?? true,
          sortOrder: formData.sortOrder || 1,
        },
        imageFile,
      });
    } else {
      updateMutation.mutate({
        formData: {
          title: formData.title,
          linkUrl: formData.linkUrl,
          adType: formData.adType || 'BANNER',
          isVisible: formData.isVisible ?? true,
          sortOrder: formData.sortOrder || 1,
          imageUrl: formData.imageUrl,
        },
        imageFile,
      });
    }
  };

  // 새로 선택한 파일 미리보기가 있으면 우선 표시, 없으면 기존 imageUrl 사용
  const displayImage = imagePreview || formData.imageUrl;
  const isSaving = isSubmitting || createMutation.isPending || updateMutation.isPending;

  // 직접 URL로 진입했거나 state가 없는 경우 — 목록에서 선택하도록 안내
  if (!adver && !isNewAdver) {
    return (
      <EmptyDetailState
        message="광고 데이터를 찾을 수 없습니다"
        backTo="/admin/adver"
      />
    );
  }

  return (
    <div>
      {/* 헤더: 제목 + 목록으로 버튼 */}
      <div className="mb-8 flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-gray-800">
            {isNewAdver ? '광고 등록' : '광고 상세'}
          </h1>
          <p className="text-gray-500 mt-2">
            {isNewAdver ? '새 광고 등록' : `광고 #${id}`}
          </p>
        </div>
        <BackButton to="/admin/adver" />
      </div>

      {/* 이미지 미리보기 카드 — 이미지가 있을 때만 표시 */}
      {displayImage && (
        <div className="bg-white rounded-xl shadow-lg p-6 mb-6">
          <h2 className="text-lg font-bold text-gray-800 mb-4">
            <i className="fas fa-image text-primary mr-2"></i>
            광고 이미지 미리보기
          </h2>
          <div className="relative aspect-[2/1] bg-gray-100 rounded-lg overflow-hidden">
            <img
              src={displayImage}
              alt="광고 미리보기"
              className="w-full h-full object-cover"
              onError={(e) => {
                // 이미지 로드 실패 시 img 태그 자체를 숨김 (broken icon 방지)
                e.currentTarget.onerror = null;
                e.currentTarget.style.display = 'none';
              }}
            />
            {!formData.isVisible && (
              <div className="absolute inset-0 bg-black bg-opacity-50 flex items-center justify-center">
                <span className="px-4 py-2 bg-gray-800 text-white text-sm font-bold rounded-full">
                  <i className="fas fa-pause-circle mr-2"></i>비활성
                </span>
              </div>
            )}
          </div>
        </div>
      )}

      {/* 폼 카드 */}
      <div className="bg-white rounded-xl shadow-lg p-8">
        <div className="space-y-6">

          <InputField
            label={<><i className="fas fa-heading text-primary mr-2"></i>광고 제목 (관리용)</>}
            value={formData.title || ''}
            onChange={(e) => setFormData({ ...formData, title: e.target.value })}
            disabled={!isEditing}
            placeholder="예: 여름 특가 이벤트"
          />

          {/* 이미지 업로드 — 편집 모드: 드래그 업로드 UI, 뷰 모드: URL 텍스트 */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              <i className="fas fa-image text-primary mr-2"></i>
              광고 이미지
            </label>
            {isEditing ? (
              <div
                className="border-2 border-dashed border-primary/40 rounded-lg p-6 text-center cursor-pointer hover:border-primary/70 transition-colors"
                onClick={() => fileInputRef.current?.click()}
              >
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={handleImageChange}
                />
                {imageFile ? (
                  <div className="text-sm text-gray-700">
                    <i className="fas fa-check-circle text-primary mr-2"></i>
                    {imageFile.name}
                    <span className="ml-2 text-gray-400">
                      ({(imageFile.size / 1024).toFixed(1)} KB)
                    </span>
                  </div>
                ) : (
                  <>
                    <i className="fas fa-cloud-upload-alt text-3xl text-gray-300 mb-2"></i>
                    <p className="text-sm text-gray-500">클릭하여 이미지 파일 선택</p>
                    {formData.imageUrl && (
                      <p className="text-xs text-gray-400 mt-1">선택하지 않으면 기존 이미지가 유지됩니다</p>
                    )}
                  </>
                )}
              </div>
            ) : (
              <input
                type="text"
                value={formData.imageUrl || '이미지 없음'}
                disabled
                className="w-full px-4 py-3 border border-gray-300 rounded-lg bg-bg-light text-gray-500"
              />
            )}
            <p className="mt-2 text-xs text-gray-500">
              <i className="fas fa-info-circle mr-1"></i>
              권장 비율: 2:1 (예: 800x400px)
            </p>
          </div>

          <InputField
            label={<><i className="fas fa-link text-primary mr-2"></i>링크 URL</>}
            type="url"
            value={formData.linkUrl || ''}
            onChange={(e) => setFormData({ ...formData, linkUrl: e.target.value })}
            disabled={!isEditing}
            placeholder="https://example.com"
            hint="광고 클릭 시 이동할 페이지 URL"
          />

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <SelectField
              label={<><i className="fas fa-layer-group text-primary mr-2"></i>광고 유형</>}
              value={formData.adType || 'BANNER'}
              onChange={(e) => {
                const adType = e.target.value;
                // BOX로 변경 시 sortOrder를 BOX 허용 범위(1~3)로 클램핑
                const sortOrder = adType === 'BOX'
                  ? Math.min(formData.sortOrder || 1, 3)
                  : formData.sortOrder || 1;
                setFormData({ ...formData, adType, sortOrder });
              }}
              disabled={!isEditing}
            >
              <option value="BANNER">상단 배너 (BANNER)</option>
              <option value="BOX">하단 박스 (BOX)</option>
            </SelectField>

            <SelectField
              label={<><i className="fas fa-toggle-on text-primary mr-2"></i>활성화 상태</>}
              value={formData.isVisible ? 'true' : 'false'}
              onChange={(e) => setFormData({ ...formData, isVisible: e.target.value === 'true' })}
              disabled={!isEditing}
            >
              <option value="true">활성</option>
              <option value="false">비활성</option>
            </SelectField>
          </div>

          {/* 표시 순서 — BOX: 1~3 버튼 선택, BANNER: 숫자 입력 */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              <i className="fas fa-sort-numeric-down text-primary mr-2"></i>
              표시 순서
              {formData.adType === 'BOX' && (
                <span className="ml-2 text-xs font-normal text-marker-orange">
                  하단 박스는 1~3까지만 설정 가능합니다
                </span>
              )}
            </label>
            {formData.adType === 'BOX' ? (
              <div className="flex gap-3">
                {[1, 2, 3].map((n) => (
                  <button
                    key={n}
                    type="button"
                    onClick={() => isEditing && setFormData({ ...formData, sortOrder: n })}
                    disabled={!isEditing}
                    className={`flex-1 py-3 rounded-lg border-2 font-bold text-lg transition-colors ${
                      formData.sortOrder === n
                        ? 'border-primary bg-primary text-white'
                        : isEditing
                          ? 'border-gray-300 text-gray-500 hover:border-primary/50'
                          : 'border-gray-200 text-gray-400 bg-bg-light'
                    }`}
                  >
                    {n}
                  </button>
                ))}
              </div>
            ) : (
              <InputField
                label=""
                type="number"
                value={formData.sortOrder || 1}
                onChange={(e) => setFormData({ ...formData, sortOrder: parseInt(e.target.value) || 1 })}
                disabled={!isEditing}
              />
            )}
          </div>

          {/* 등록일·수정일 — 기존 광고일 때만 표시 */}
          {!isNewAdver && formData.createdAt && formData.updatedAt && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <InputField
                label={<><i className="fas fa-calendar-plus text-primary mr-2"></i>등록일</>}
                value={formatDateTime(formData.createdAt)}
                disabled
              />
              <InputField
                label={<><i className="fas fa-calendar-check text-primary mr-2"></i>수정일</>}
                value={formatDateTime(formData.updatedAt)}
                disabled
              />
            </div>
          )}
        </div>

        <DetailActionButtons
          isNew={isNewAdver}
          isEditing={isEditing}
          isSaving={isSaving}
          onEdit={handleEdit}
          onSave={handleSave}
          onCancelEdit={handleCancelEdit}
          onDelete={() => setShowDeleteModal(true)}
        />
      </div>

      {showDeleteModal && (
        <ConfirmModal
          title="광고를 삭제하시겠습니까?"
          confirmLabel="삭제"
          isLoading={deleteMutation.isPending}
          onConfirm={() => { deleteMutation.mutate(); setShowDeleteModal(false); }}
          onCancel={() => setShowDeleteModal(false)}
        />
      )}
    </div>
  );
}
